// Considered: the editor's old "Aggregation & Weight" CollapsibleSectionAtom + DropdownAtom — rejected because
// one dropdown fused two questions (which result wins, how much a member counts) into one list. See spec Reuse Audit.
import React, { useId } from 'react';
import { useTranslation } from 'localization';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import type { AggregationMethod, RollupWeighting } from 'types/spherePoints';
import { fromStored, toStored, type RollupWins } from './combineModel';
import './RollupCombineSection.scss';

export interface RollupCombineSectionProps {
  aggregationMethod: AggregationMethod;
  weighting: RollupWeighting;
  /** Receives the stored pair, already mapped through `toStored`. */
  onChange: (updates: { aggregationMethod: AggregationMethod; weighting: RollupWeighting }) => void;
  /** Viewers see the current choice but cannot change it. */
  disabled?: boolean;
  className?: string;
  /** The Trust guardrails group; it shares this card, below a divider. */
  children?: React.ReactNode;
}

const WINS_VALUES: RollupWins[] = ['worst', 'balanced', 'majority'];
const WEIGHTING_VALUES: RollupWeighting[] = ['equal', 'influence'];

const WINS_DEFAULTS: Record<RollupWins, { label: string; help: string }> = {
  worst: {
    label: 'Worst wins',
    help: "The most severe member status becomes this factor's.",
  },
  balanced: {
    label: 'Balanced',
    help: 'Member statuses are scored and banded. Members with no reading are left out, never scored as zero.',
  },
  majority: {
    label: 'Majority',
    help: 'The most common status wins. Ties go to the more severe status.',
  },
};

const WEIGHTING_DEFAULTS: Record<RollupWeighting, { label: string; help: string }> = {
  equal: {
    label: 'Equally',
    help: 'Every member counts once, however it arrived.',
  },
  influence: {
    label: 'By influence',
    help: 'Members count by their weight. Edit weights in the roster above.',
  },
};

const RollupCombineSection: React.FC<RollupCombineSectionProps> = ({ aggregationMethod, weighting, onChange, disabled = false, className, children }) => {
  const { t } = useTranslation();
  const uid = useId();
  const axes = fromStored({ aggregationMethod, weighting });

  const winsOptions = WINS_VALUES.map((value) => ({
    value,
    label: t(`statusFactors.combine.wins.${value}.label`, WINS_DEFAULTS[value].label),
    title: t(`statusFactors.combine.wins.${value}.help`, WINS_DEFAULTS[value].help),
    disabled,
  }));
  const weightingOptions = WEIGHTING_VALUES.map((value) => ({
    value,
    label: t(`statusFactors.combine.weighting.${value}.label`, WEIGHTING_DEFAULTS[value].label),
    disabled,
  }));

  const winsLabelId = `${uid}-wins-label`;
  const weightingLabelId = `${uid}-weighting-label`;
  const classes = ['rollup-combine-section', className].filter(Boolean).join(' ');

  return (
    <section className={classes} aria-labelledby={`${uid}-heading`} data-testid="rollup-combine-section">
      <h3 id={`${uid}-heading`} className="rollup-combine-section__heading">
        {t('statusFactors.combine.heading', 'How members combine')}
      </h3>

      <div className="rollup-combine-section__body">
        <div className="rollup-combine-section__axes">
          <div className="rollup-combine-section__axis">
            <span id={winsLabelId} className="rollup-combine-section__label">
              {t('statusFactors.combine.wins.label', 'Which result wins')}
            </span>
            <SegmentedControlAtom<RollupWins>
              mode="single"
              variant="pills"
              tone="quiet"
              size="compact"
              options={winsOptions}
              value={axes.wins}
              onChange={(v) => onChange(toStored({ wins: v as RollupWins, weighting: axes.weighting }))}
              ariaLabelledBy={winsLabelId}
              renderAs="button"
              data-testid="rollup-combine-wins"
            />
          </div>

          <div className="rollup-combine-section__axis">
            <span id={weightingLabelId} className="rollup-combine-section__label">
              {t('statusFactors.combine.weighting.label', 'How much each member counts')}
            </span>
            <SegmentedControlAtom<RollupWeighting>
              mode="single"
              tone="quiet"
              size="compact"
              options={weightingOptions}
              value={axes.weighting}
              onChange={(v) => onChange(toStored({ wins: axes.wins, weighting: v as RollupWeighting }))}
              ariaLabelledBy={weightingLabelId}
              renderAs="button"
              data-testid="rollup-combine-weighting"
            />
          </div>
        </div>

        <div className="rollup-combine-section__helps">
          <p className="rollup-combine-section__help" data-testid="rollup-combine-wins-help">
            {t(`statusFactors.combine.wins.${axes.wins}.help`, WINS_DEFAULTS[axes.wins].help)}
          </p>
          <p className="rollup-combine-section__help" data-testid="rollup-combine-weighting-help">
            {t(`statusFactors.combine.weighting.${axes.weighting}.help`, WEIGHTING_DEFAULTS[axes.weighting].help)}
          </p>
        </div>

        {children && <div className="rollup-combine-section__guardrails">{children}</div>}
      </div>
    </section>
  );
};

RollupCombineSection.displayName = 'RollupCombineSection';

export default RollupCombineSection;
